import type { DispatchRecord, PlatformInfo, PlatformId, PostMetrics } from "../api";

/**
 * Cross-platform analytics: instead of five parallel metric columns, tell the
 * story of how one piece of content performed *as a campaign* across the
 * platforms it went to — total reach, total engagement, and which platform
 * carried it.
 *
 * Honest about the data we actually have: only some platforms report `views`
 * (e.g. YouTube), so we never build a "reach share" out of them — that would
 * make the one platform we measure look like it won by default. Engagement
 * (likes + comments + shares) is reported by IG / YouTube / Facebook alike, so
 * the platform-share breakdown is built on engagement, and views are surfaced
 * separately as reach.
 */

export interface PlatformShare {
  id: PlatformId;
  name: string;
  accent: string;
  engagement: number;
  /** Share of the post's total engagement, 0–100. */
  pct: number;
}

export interface PostBreakdown {
  id: string;
  title: string;
  at: number;
  /** Sum of views across platforms that report them, or null if none do. */
  totalViews: number | null;
  /** Sum of likes + comments + shares across every platform. */
  totalEngagement: number;
  /** Per-platform engagement, highest first (only platforms with engagement). */
  shares: PlatformShare[];
  /** The platform that drove the most engagement, or null when there is none. */
  top: PlatformShare | null;
}

function engagementOf(m: PostMetrics | undefined): number {
  if (!m) return 0;
  return (m.likes ?? 0) + (m.comments ?? 0) + (m.shares ?? 0);
}

/** Build the cross-platform breakdown for a single published post. */
export function postBreakdown(rec: DispatchRecord, platforms: PlatformInfo[]): PostBreakdown {
  const byId = new Map(platforms.map((p) => [p.id, p]));
  let totalEngagement = 0;
  let totalViews = 0;
  let anyViews = false;

  const rows: PlatformShare[] = [];
  for (const [id, m] of Object.entries(rec.metrics ?? {})) {
    if (typeof m?.views === "number") {
      totalViews += m.views;
      anyViews = true;
    }
    const engagement = engagementOf(m);
    totalEngagement += engagement;
    if (engagement > 0) {
      const p = byId.get(id as PlatformId);
      rows.push({
        id: id as PlatformId,
        name: p?.name ?? id,
        accent: p?.accent ?? "var(--muted)",
        engagement,
        pct: 0,
      });
    }
  }

  const shares = rows
    .map((r) => ({ ...r, pct: totalEngagement > 0 ? (r.engagement / totalEngagement) * 100 : 0 }))
    .sort((a, b) => b.engagement - a.engagement);

  return {
    id: rec.id,
    title: rec.title,
    at: rec.at,
    totalViews: anyViews ? totalViews : null,
    totalEngagement,
    shares,
    top: shares[0] ?? null,
  };
}

const DAY_MS = 86_400_000;

/** Per-day trend series (oldest → newest), one value per day over the last
 * `days` days, bucketed by the LOCAL calendar day a post was published. Views
 * and engagement are attributed to the day the post that earned them went out —
 * the honest available signal, since we hold current metric snapshots, not a
 * per-day history of metric growth. Only published records count. */
export interface DailySeries {
  posts: number[];
  views: number[];
  engagement: number[];
}

export function dailySeries(history: DispatchRecord[], days = 30): DailySeries {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const posts = new Array<number>(days).fill(0);
  const views = new Array<number>(days).fill(0);
  const engagement = new Array<number>(days).fill(0);

  for (const rec of history) {
    if (!rec.results.some((r) => r.ok)) continue; // published posts only
    const d = new Date(rec.at);
    const recDayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const idx = days - 1 - Math.round((todayStart - recDayStart) / DAY_MS);
    if (idx < 0 || idx >= days) continue;
    posts[idx] = (posts[idx] ?? 0) + 1;
    for (const m of Object.values(rec.metrics ?? {})) {
      if (typeof m?.views === "number") views[idx] = (views[idx] ?? 0) + m.views;
      engagement[idx] = (engagement[idx] ?? 0) + engagementOf(m);
    }
  }
  return { posts, views, engagement };
}

/** Total engagement + views per platform across all history, for a
 * which-platform-performs comparison. Only platforms with some reported data
 * are returned; highest engagement first. */
export interface PlatformTotal {
  id: PlatformId;
  name: string;
  accent: string;
  engagement: number;
  views: number;
}

export function platformTotals(
  history: DispatchRecord[],
  platforms: PlatformInfo[],
): PlatformTotal[] {
  const byId = new Map(platforms.map((p) => [p.id, p]));
  const acc = new Map<PlatformId, { engagement: number; views: number }>();
  for (const rec of history) {
    for (const [id, m] of Object.entries(rec.metrics ?? {})) {
      const cur = acc.get(id as PlatformId) ?? { engagement: 0, views: 0 };
      cur.engagement += engagementOf(m);
      if (typeof m?.views === "number") cur.views += m.views;
      acc.set(id as PlatformId, cur);
    }
  }
  return [...acc.entries()]
    .map(([id, v]) => {
      const p = byId.get(id);
      return { id, name: p?.name ?? id, accent: p?.accent ?? "var(--muted)", ...v };
    })
    .filter((r) => r.engagement > 0 || r.views > 0)
    .sort((a, b) => b.engagement - a.engagement || b.views - a.views);
}
